// Flags weeks where a metric's MMM value diverges from its canonical (BPM) value by
// more than a per-point threshold, for highlighting directly on the Portfolio/Channels
// charts. A fixed rule (not the compare-to-canonical SQL's user-chosen threshold,
// deliberately removed there -- see GroundTruthSqlPanel) per Sean's explicit spec
// (2026-08-23): |MMM/BPM - 1| < 0.5% is fine, 0.5-1% is worth noting, > 1% needs
// investigating.
//
// A prior version of this file used a robust (median + MAD) statistical outlier score
// instead of a fixed threshold, to flag a sustained divergent REGION without a magic
// percentage. Replaced after real review against `away` data surfaced two failure
// modes in opposite directions: it over-warned on a run of exceedingly minor spend
// differences (statistically unusual relative to that series' own tiny noise floor,
// but not practically meaningful) and under-warned on major impressions differences
// (a large enough contiguous divergent block still drags its own median/MAD along with
// it in some cases). A simple, symmetric, per-point percentage rule doesn't have either
// failure mode -- what counts as "needs investigating" is exactly what the number says,
// not relative to how noisy the rest of the series happens to be.

export type DivergenceSeverity = 'note' | 'investigate';

export interface DivergenceRange {
  from: string;
  to: string;
  severity: DivergenceSeverity;
}

const NOTE_THRESHOLD = 0.005; // 0.5%
const INVESTIGATE_THRESHOLD = 0.01; // 1%

// |MMM / BPM - 1|, matching the exact formula GroundTruthSqlPanel's aggregate deviation
// readout already uses. BPM of 0 with nonzero MMM is the maximal possible divergence
// (MMM claims delivery the canonical DB shows none of) -- treated as "investigate", not
// a division-by-zero no-op. `bpm` MUST already be a real, confirmed 0 by the time it
// gets here, never a stand-in for "no ground truth data this week" (WeekRow's own doc
// comment: those fields are undefined, not 0, when absent) -- callers coalescing
// undefined to 0 before calling computeDivergentRanges would make every week lacking
// ground truth read as maximal divergence instead of no signal at all.
const percentDiff = (mmm: number, bpm: number): number => {
  if (bpm === 0) {
    return mmm === 0 ? 0 : Infinity;
  }
  return Math.abs(mmm / bpm - 1);
};

export const severityFor = (diff: number): DivergenceSeverity | undefined => {
  if (diff >= INVESTIGATE_THRESHOLD) return 'investigate';
  if (diff >= NOTE_THRESHOLD) return 'note';
  return undefined;
};

// `xValue` should be whatever value the chart's category x-axis actually keys on
// (DataQaChart's XAxis uses WeekRow.weekLabel, not weekIso) -- ranges are returned
// using that same value so they're guaranteed to match up when passed straight into
// DataQaChart's x1/x2. Adjacent weeks sharing the same severity are merged into one
// range so the chart shows a handful of shaded bands, not one rectangle per week.
export const computeDivergentRanges = (
  weeks: { xValue: string; mmm: number; bpm: number | undefined }[]
): DivergenceRange[] => {
  const ranges: DivergenceRange[] = [];
  let current: { severity: DivergenceSeverity; start: string } | undefined;

  weeks.forEach((week, i) => {
    // Absent ground truth for this week (not a confirmed 0) has no divergence signal
    // at all -- see percentDiff's doc comment. Callers must pass bpmX ?? undefined
    // straight through, not bpmX ?? 0.
    const severity =
      week.bpm === undefined ? undefined : severityFor(percentDiff(week.mmm, week.bpm));
    if (severity === current?.severity) {
      return;
    }
    if (current) {
      ranges.push({ from: current.start, to: weeks[i - 1].xValue, severity: current.severity });
    }
    current = severity ? { severity, start: week.xValue } : undefined;
  });
  if (current) {
    ranges.push({
      from: current.start,
      to: weeks[weeks.length - 1].xValue,
      severity: current.severity
    });
  }
  return ranges;
};
